import { Box, Chip, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { formatDateDMY } from '@/utils/date'
import type { WidgetProps } from './WidgetProps'
import { EmptyHint } from './EmptyHint'

interface GrantPayload {
  id: number
  plan_title: string | null
  lessons_remaining: number
  lessons_total: number
  expires_at: string | null
}

export function SubscriptionStatusWidget({ payload }: WidgetProps) {
  const { t } = useTranslation()

  if (payload.empty === true) {
    return (
      <EmptyHint hint={typeof payload.hint === 'string' ? payload.hint : undefined} />
    )
  }

  const grant = (payload.grant ?? null) as GrantPayload | null
  const balance = String(payload.balance_amount ?? '0')

  const expiresSoon =
    grant?.expires_at != null && dayjs(grant.expires_at).diff(dayjs(), 'day') <= 7
  const lowLessons = grant !== null && grant.lessons_remaining <= 1
  const warn = expiresSoon || lowLessons

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('dashboard.widgets.subscription_status.balance')}
        </Typography>
        <Typography variant="h6" fontWeight={700} color={balance.startsWith('-') ? 'warning.main' : 'text.primary'}>
          {balance}
        </Typography>
      </Box>
      {grant ? (
        <Box>
          {grant.plan_title && (
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {grant.plan_title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              color={warn ? 'warning' : 'success'}
              label={`${t('dashboard.widgets.subscription_status.lessonsLeft')}: ${grant.lessons_remaining}/${grant.lessons_total}`}
            />
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.widgets.subscription_status.expiresAt')}:{' '}
              {formatDateDMY(grant.expires_at)}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.widgets.subscription_status.noGrant')}
        </Typography>
      )}
    </Stack>
  )
}
